import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { isApiError, rethrowIfUnauthorized, shouldShowApiErrorToast } from '../api/errors'
import {
  createReferenceFolder,
  deleteReferenceFolder,
  deleteReferenceImage,
  listReferenceFolders,
  listReferenceImages,
  moveReferenceFolder,
  moveReferenceImage,
  type ReferenceImageItem,
  refreshReferenceImageUrlByObjectKey,
  renameReferenceFolder,
  renameReferenceImage,
  syncReferenceCloudIndex,
} from '../api/referenceImages'
import { resolveReferenceImageDisplayUrl } from '../api/referenceUpload'
import { uploadGalleryImageToCloud } from '../api/referenceUpload'
import { AsyncPullLoadingIndicator } from '../components/async/AsyncPullLoadingIndicator'
import {
  ChromePrimaryButton,
  ChromeTextInput,
  ChromeToolbarButton,
  ChromeToolbarDangerButton,
} from '../components/ChromeActions'
import { TimelinePullIndicator } from '../components/create/TimelineRefreshSurfaces'
import type { ImageLightboxItem } from '../components/ImageLightbox'
import { LibraryFolderTreeDrawer } from '../components/library/LibraryFolderTreeDrawer'
import { LibraryFolderTreeView } from '../components/library/LibraryFolderTreeView'
import {
  LibraryAssetsPane,
  LibraryFolderSidebarToolbar,
  LibraryManagementPanel,
  LibraryManagementToolbar,
} from '../components/library/LibraryManagementSurface'
import { ThemedLibraryState } from '../components/library/ThemedLibraryState'
import { BlockingModal, ThemeModal } from '../components/modal'
import { ScrollProximitySentinel } from '../components/ui/ScrollProximitySentinel'
import { useImageLightbox } from '../context/ImageLightboxContext'
import { useToast } from '../context/ToastContext'
import { useBlockingAction } from '../hooks/useBlockingAction'
import { useThrottledCallback } from '../hooks/useRateLimitedCallback'
import { useScrollPullToRefresh } from '../hooks/useScrollPullToRefresh'
import { USER_PERMISSION, useUserPerm } from '../hooks/useUserPerm'
import { ContentSkeleton } from '../layouts/ContentSkeleton'
import { formatGalleryDateTime } from '../lib/dateDisplay'
import {
  buildLibrarySidebarTreeModel,
  collectFolderPathsFromSidebarModel,
  filterItemsByFolderPath,
} from '../lib/folderTree'
import { isAbortError } from '../lib/isAbortError'
import { buildLibraryPageBreadcrumbs } from '../lib/libraryBreadcrumbs'
import {
  getLibraryPageSessionCache,
  isLibrarySessionCacheFresh,
  setLibraryPageSessionCache,
} from '../lib/libraryPageSessionCache'
import {
  formatNewFolderPreviewDisplayPath,
  isSystemReferenceUploadRootFolder,
  isUnderReferenceUploadSubtreeUiPath,
  normalizeLibraryUiFolderPath,
  REFERENCE_IMAGE_MAX_BYTES,
  resolveChildFolderUnderParent,
} from '../lib/libraryPaths'
import { persistLibraryFolderPath, readStoredLibraryFolderPath } from '../lib/libraryUiPersistence'
import { refreshReferenceUrlOnce } from '../lib/referenceUrlRefresh'
import { useTheme } from '../theme/ThemeContext'

const PAGE_SIZE = 40

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '-'
  if (n < 1024) return `${Math.round(n)} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

export function LibraryPage() {
  const location = useLocation()
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const { showToast } = useToast()
  const { blockingState, runBlocking, blocking } = useBlockingAction()
  const canWriteLibrary = useUserPerm(USER_PERMISSION.creationTaskSubmit)
  const libraryScrollRef = useRef<HTMLDivElement | null>(null)
  const [libraryPullAttachSignal, setLibraryPullAttachSignal] = useState(0)
  const setLibraryScrollPort = useCallback((node: HTMLDivElement | null) => {
    libraryScrollRef.current = node
    setLibraryPullAttachSignal((n) => n + 1)
  }, [])

  const [items, setItems] = useState<ReferenceImageItem[]>([])
  const [folderPaths, setFolderPaths] = useState<string[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const {
    open: openLibraryLightbox,
    close: closeLibraryLightbox,
    sourcePublicId: libraryLightboxSourcePublicId,
  } = useImageLightbox()
  const [selectedItem, setSelectedItem] = useState<ReferenceImageItem | null>(null)
  const [selectedFolderPath, setSelectedFolderPath] = useState(readStoredLibraryFolderPath)
  const [folderDrawerOpen, setFolderDrawerOpen] = useState(false)

  const [createFolderOpen, setCreateFolderOpen] = useState(false)
  const [createFolderDraft, setCreateFolderDraft] = useState('')
  const [createFolderParentPath, setCreateFolderParentPath] = useState('')

  const [renameOpen, setRenameOpen] = useState(false)
  const [renameDraft, setRenameDraft] = useState('')

  const [moveOpen, setMoveOpen] = useState(false)
  const [moveDestPath, setMoveDestPath] = useState('')

  const [deleteImageOpen, setDeleteImageOpen] = useState(false)
  const [deleteFolderOpen, setDeleteFolderOpen] = useState(false)
  const [renameFolderOpen, setRenameFolderOpen] = useState(false)
  const [renameFolderDraft, setRenameFolderDraft] = useState('')
  const [moveFolderOpen, setMoveFolderOpen] = useState(false)
  const [moveFolderTargetPath, setMoveFolderTargetPath] = useState('')
  /** PC：右侧管理栏展开（收起后中间图库区域更宽） */
  const [libraryManagementOpen, setLibraryManagementOpen] = useState(true)

  /** 图库图片上传：隐藏 file input + 上传中状态 */
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const displayItems = useMemo(() => items, [items])

  const treeModel = useMemo(
    () => buildLibrarySidebarTreeModel(displayItems, folderPaths),
    [displayItems, folderPaths],
  )

  /** 合并后端文件夹列表，避免仅来自 items 的树一时缺节点导致选中路径被清空、文件夹操作全禁用 */
  const knownFolderPaths = useMemo(() => {
    const out = new Set(collectFolderPathsFromSidebarModel(treeModel))
    for (const p of folderPaths) {
      const n = normalizeLibraryUiFolderPath(p)
      if (n) out.add(n)
    }
    return out
  }, [folderPaths, treeModel])

  const folderDeleteBlocked = useMemo(
    () => isSystemReferenceUploadRootFolder(selectedFolderPath),
    [selectedFolderPath],
  )

  const createFolderBlocked = useMemo(
    () => isUnderReferenceUploadSubtreeUiPath(selectedFolderPath),
    [selectedFolderPath],
  )

  const folderRenameBlocked = useMemo(
    () =>
      !normalizeLibraryUiFolderPath(selectedFolderPath) ||
      isUnderReferenceUploadSubtreeUiPath(selectedFolderPath),
    [selectedFolderPath],
  )

  const folderMoveBlocked = useMemo(
    () =>
      !normalizeLibraryUiFolderPath(selectedFolderPath) ||
      isUnderReferenceUploadSubtreeUiPath(selectedFolderPath),
    [selectedFolderPath],
  )

  const renameFolderParentUiPath = useMemo(() => {
    const segs = normalizeLibraryUiFolderPath(selectedFolderPath).split('/').filter(Boolean)
    if (segs.length <= 1) return ''
    return segs.slice(0, -1).join('/')
  }, [selectedFolderPath])

  const renameFolderPathPreviewDisplay = useMemo(() => {
    if (!renameFolderOpen || folderRenameBlocked) return ''
    return formatNewFolderPreviewDisplayPath(renameFolderParentUiPath, renameFolderDraft)
  }, [folderRenameBlocked, renameFolderDraft, renameFolderOpen, renameFolderParentUiPath])

  const createFolderPathPreviewDisplay = useMemo(() => {
    if (isUnderReferenceUploadSubtreeUiPath(createFolderParentPath)) return ''
    return formatNewFolderPreviewDisplayPath(createFolderParentPath, createFolderDraft)
  }, [createFolderDraft, createFolderParentPath])

  useEffect(() => {
    persistLibraryFolderPath(selectedFolderPath)
  }, [selectedFolderPath])

  useEffect(() => {
    if (loading) return
    setSelectedFolderPath((current) => {
      if (current === '') return current
      const norm = normalizeLibraryUiFolderPath(current)
      if (knownFolderPaths.has(norm)) return norm
      /** 「上传的参考图」子树由目录树顶置根提供，不在 folders API 列表中，勿被弹回根目录 */
      if (isUnderReferenceUploadSubtreeUiPath(norm)) return norm
      return ''
    })
  }, [loading, knownFolderPaths])

  const filtered = useMemo(
    () => filterItemsByFolderPath(displayItems, selectedFolderPath),
    [displayItems, selectedFolderPath],
  )

  const loadErrorToastKeyRef = useRef<string | null>(null)

  const loadFolders = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await listReferenceFolders(signal)
      if (signal?.aborted) return
      setFolderPaths(res.paths ?? [])
    } catch (e) {
      if (signal?.aborted || isAbortError(e)) return
      rethrowIfUnauthorized(e)
      const msg = isApiError(e) ? e.message : '文件夹加载失败'
      if (shouldShowApiErrorToast(e)) {
        showToast({ message: msg, variant: 'warning' })
      }
    }
  }, [showToast])

  const loadPage = useCallback(
    async (targetPage: number, append: boolean, signal?: AbortSignal, folderPath?: string): Promise<boolean> => {
      if (append) setLoadingMore(true)
      else setLoading(true)
      setError(null)
      try {
        const res = await listReferenceImages(targetPage, PAGE_SIZE, signal, folderPath)
        if (signal?.aborted) return false
        setTotal(res.total)
        setPage(targetPage)
        setItems((prev) => (append ? [...prev, ...res.items] : res.items))
        return true
      } catch (e) {
        if (signal?.aborted || isAbortError(e)) return false
        rethrowIfUnauthorized(e)
        const msg = isApiError(e) ? e.message : '加载失败，请稍后重试'
        setError(msg)
        if (!append) {
          setItems([])
          setTotal(0)
        }
        if (shouldShowApiErrorToast(e) && loadErrorToastKeyRef.current !== msg) {
          loadErrorToastKeyRef.current = msg
          showToast({ message: msg, variant: 'warning' })
        }
        return false
      } finally {
        if (append) {
          setLoadingMore(false)
        } else {
          setLoading(false)
        }
      }
    },
    [showToast],
  )

  const loadFoldersRef = useRef(loadFolders)
  const loadPageRef = useRef(loadPage)
  loadFoldersRef.current = loadFolders
  loadPageRef.current = loadPage

  const libraryListSyncAbortRef = useRef<AbortController | null>(null)
  const hasInitializedRef = useRef(false)
  const lastFetchedFolderRef = useRef<string | null>(null)
  const selectedFolderPathRef = useRef(selectedFolderPath)
  const selectedItemRef = useRef(selectedItem)
  selectedFolderPathRef.current = selectedFolderPath
  selectedItemRef.current = selectedItem

  const writeLibrarySessionCache = useCallback(
    (patch: {
      folderPath: string
      items: ReferenceImageItem[]
      folderPaths: string[]
      total: number
      page: number
    }) => {
      setLibraryPageSessionCache({
        folderPath: normalizeLibraryUiFolderPath(patch.folderPath),
        items: patch.items,
        folderPaths: patch.folderPaths,
        total: patch.total,
        page: patch.page,
        loadedAt: Date.now(),
        scrollTop: libraryScrollRef.current?.scrollTop ?? 0,
        selectedPublicId: selectedItemRef.current?.publicId ?? null,
      })
    },
    [],
  )

  /** 按文件夹加载图片列表 */
  const refreshLibraryData = useCallback(async (folderPath?: string): Promise<boolean> => {
    libraryListSyncAbortRef.current?.abort()
    const ac = new AbortController()
    libraryListSyncAbortRef.current = ac
    const signal = ac.signal
    const folder = normalizeLibraryUiFolderPath(folderPath ?? selectedFolderPathRef.current)
    loadErrorToastKeyRef.current = null
    const results = await Promise.all([
      loadFoldersRef.current(signal),
      loadPageRef.current(1, false, signal, folder),
    ])
    const ok = results[1] !== false
    if (ok && !signal.aborted) {
      hasInitializedRef.current = true
      lastFetchedFolderRef.current = folder
    }
    return ok
  }, [])

  /** 进入图库路由：优先恢复同会话缓存（含参考图目录），避免从其它页返回即全量刷新 */
  useEffect(() => {
    if (location.pathname !== '/library') return

    const folder = normalizeLibraryUiFolderPath(selectedFolderPathRef.current)
    const cached = getLibraryPageSessionCache()
    if (cached && isLibrarySessionCacheFresh(cached, folder)) {
      setFolderPaths(cached.folderPaths)
      setItems(cached.items)
      setTotal(cached.total)
      setPage(cached.page)
      setError(null)
      setLoading(false)
      setLoadingMore(false)
      const hit = cached.selectedPublicId
        ? cached.items.find((it) => it.publicId === cached.selectedPublicId) ?? null
        : null
      setSelectedItem(hit)
      hasInitializedRef.current = true
      lastFetchedFolderRef.current = folder
      const scrollTop = cached.scrollTop
      requestAnimationFrame(() => {
        const el = libraryScrollRef.current
        if (el) el.scrollTop = scrollTop
      })
      return
    }

    void refreshLibraryData(folder)
    return () => {
      libraryListSyncAbortRef.current?.abort()
      libraryListSyncAbortRef.current = null
    }
  }, [location.pathname, refreshLibraryData])

  /** 在图库内切换文件夹：重新拉取该目录（跳过与上次已加载相同路径） */
  useEffect(() => {
    if (location.pathname !== '/library') return
    if (!hasInitializedRef.current) return
    const norm = normalizeLibraryUiFolderPath(selectedFolderPath)
    if (lastFetchedFolderRef.current === norm) return
    lastFetchedFolderRef.current = norm
    void refreshLibraryData(norm)
    return () => {
      libraryListSyncAbortRef.current?.abort()
    }
  }, [location.pathname, selectedFolderPath, refreshLibraryData])

  /** 列表/目录更新后写入会话缓存；离开路由前保留滚动与选中项 */
  useEffect(() => {
    if (location.pathname !== '/library') return
    if (loading || items.length === 0) return
    writeLibrarySessionCache({
      folderPath: selectedFolderPath,
      items,
      folderPaths,
      total,
      page,
    })
  }, [location.pathname, loading, items, folderPaths, total, page, selectedFolderPath, writeLibrarySessionCache])

  useEffect(() => {
    return () => {
      const cache = getLibraryPageSessionCache()
      if (!cache || cache.items.length === 0) return
      setLibraryPageSessionCache({
        ...cache,
        scrollTop: libraryScrollRef.current?.scrollTop ?? cache.scrollTop,
        selectedPublicId: selectedItemRef.current?.publicId ?? null,
        loadedAt: cache.loadedAt,
      })
    }
  }, [])

  const libraryRouteRef = useRef(location.pathname)
  libraryRouteRef.current = location.pathname

  const libraryHiddenAtRef = useRef<number | null>(null)

  /**
   * 避免每次切回标签页都全量拉取：仅当页面曾后台超过 2 分钟、或从往返缓存恢复时再同步。
   */
  useEffect(() => {
    const STALE_MS = 120_000
    let t: number | undefined
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        libraryHiddenAtRef.current = Date.now()
        return
      }
      if (libraryRouteRef.current !== '/library') return
      if (t) window.clearTimeout(t)
      t = window.setTimeout(() => {
        t = undefined
        if (libraryRouteRef.current !== '/library') return
        const hiddenAt = libraryHiddenAtRef.current
        libraryHiddenAtRef.current = null
        if (hiddenAt == null) return
        if (Date.now() - hiddenAt < STALE_MS) return
        void refreshLibraryData(selectedFolderPath)
      }, 200)
    }
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return
      if (libraryRouteRef.current !== '/library') return
      void refreshLibraryData(selectedFolderPath)
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      if (t) window.clearTimeout(t)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [refreshLibraryData, selectedFolderPath])

  const loadMore = useCallback(() => {
    if (loading || loadingMore || items.length >= total || items.length === 0) return
    void loadPage(page + 1, true, undefined, selectedFolderPath)
  }, [loadPage, loading, loadingMore, items.length, total, page, selectedFolderPath])
  const throttledLoadMore = useThrottledCallback(loadMore, 800)

  useEffect(() => {
    if (!selectedItem) return
    const next = displayItems.find((item) => item.publicId === selectedItem.publicId) ?? null
    setSelectedItem(next)
  }, [displayItems, selectedItem])

  const openCreateFolderModal = useCallback(
    (folderParentPath?: string) => {
      const parent = folderParentPath ?? selectedFolderPath
      if (isUnderReferenceUploadSubtreeUiPath(parent)) {
        showToast({ message: '本地参考图目录下不可新建子文件夹', variant: 'warning' })
        return
      }
      closeLibraryLightbox()
      setCreateFolderDraft('')
      setCreateFolderParentPath(parent)
      setCreateFolderOpen(true)
    },
    [selectedFolderPath, showToast],
  )

  const submitCreateFolder = useCallback(async () => {
    if (isUnderReferenceUploadSubtreeUiPath(createFolderParentPath)) {
      showToast({ message: '本地参考图目录下不可新建子文件夹', variant: 'warning' })
      return
    }
    const resolved = resolveChildFolderUnderParent(createFolderParentPath, createFolderDraft)
    if (!resolved) {
      showToast({
        message: createFolderDraft.trim() ? '文件夹名称不能含 / 或 ..' : '请输入文件夹名称',
        variant: 'warning',
      })
      return
    }
    await runBlocking(
      async () => {
        try {
          await createReferenceFolder(resolved)
          showToast({ message: `已创建文件夹：${resolved}`, variant: 'success' })
          setCreateFolderOpen(false)
          setCreateFolderDraft('')
          void refreshLibraryData(selectedFolderPath)
        } catch (e) {
          showToast({ message: isApiError(e) ? e.message : '创建文件夹失败', variant: 'error' })
        }
      },
      {
        title: '新建文件夹',
        message: '正在创建文件夹，请稍候…',
        detail: resolved,
      },
    )
  }, [createFolderDraft, createFolderParentPath, refreshLibraryData, runBlocking, selectedFolderPath, showToast])

  const openRenameModal = useCallback(() => {
    if (!selectedItem) return
    closeLibraryLightbox()
    setRenameDraft(selectedItem.name)
    setRenameOpen(true)
  }, [selectedItem])

  const submitRename = useCallback(async () => {
    if (!selectedItem) return
    const nextName = renameDraft.trim()
    if (!nextName) {
      showToast({ message: '请输入文件名', variant: 'warning' })
      return
    }
    if (nextName === selectedItem.name) {
      setRenameOpen(false)
      return
    }
    await runBlocking(
      async () => {
        try {
          await renameReferenceImage(selectedItem.publicId, nextName)
          showToast({ message: '重命名成功', variant: 'success' })
          setRenameOpen(false)
          void refreshLibraryData()
        } catch (e) {
          showToast({ message: isApiError(e) ? e.message : '重命名失败', variant: 'error' })
        }
      },
      {
        title: '重命名',
        message: '正在保存名称，请稍候…',
        detail: nextName,
      },
    )
  }, [refreshLibraryData, renameDraft, runBlocking, selectedItem, showToast])

  const moveSourcePath = selectedItem ? selectedItem.folderPath : ''

  const openMoveModal = useCallback(() => {
    if (!selectedItem) return
    closeLibraryLightbox()
    setMoveDestPath(selectedItem.folderPath)
    setMoveOpen(true)
  }, [selectedItem])

  const openDeleteImageModal = useCallback(() => {
    closeLibraryLightbox()
    setDeleteImageOpen(true)
  }, [])

  const openDeleteFolderModal = useCallback(() => {
    closeLibraryLightbox()
    setDeleteFolderOpen(true)
  }, [])

  const openRenameFolderModal = useCallback(() => {
    if (folderRenameBlocked) {
      showToast({
        message: !normalizeLibraryUiFolderPath(selectedFolderPath)
          ? '请先进入要重命名的文件夹'
          : '该目录不可重命名',
        variant: 'warning',
      })
      return
    }
    closeLibraryLightbox()
    const segments = normalizeLibraryUiFolderPath(selectedFolderPath).split('/').filter(Boolean)
    setRenameFolderDraft(segments.at(-1) ?? '')
    setRenameFolderOpen(true)
  }, [folderRenameBlocked, selectedFolderPath, showToast])

  const submitRenameFolder = useCallback(async () => {
    if (folderRenameBlocked) return
    const path = normalizeLibraryUiFolderPath(selectedFolderPath)
    const segments = path.split('/').filter(Boolean)
    const parentUi = segments.length <= 1 ? '' : segments.slice(0, -1).join('/')
    const nextLeaf = renameFolderDraft.trim()
    if (!nextLeaf) {
      showToast({ message: '请输入文件夹名称', variant: 'warning' })
      return
    }
    const resolved = resolveChildFolderUnderParent(parentUi, nextLeaf)
    if (!resolved) {
      showToast({ message: '文件夹名称不能含 / 或 ..', variant: 'warning' })
      return
    }
    const oldLeaf = segments.at(-1) ?? ''
    if (nextLeaf === oldLeaf) {
      setRenameFolderOpen(false)
      return
    }
    await runBlocking(
      async () => {
        try {
          await renameReferenceFolder(path, nextLeaf)
          showToast({ message: '文件夹已更新', variant: 'success' })
          setRenameFolderOpen(false)
          setSelectedFolderPath(resolved)
          void refreshLibraryData()
        } catch (e) {
          showToast({ message: isApiError(e) ? e.message : '重命名文件夹失败', variant: 'error' })
        }
      },
      {
        title: '重命名文件夹',
        message: '正在迁移文件与占位…',
        detail: resolved,
      },
    )
  }, [
    folderRenameBlocked,
    refreshLibraryData,
    renameFolderDraft,
    runBlocking,
    selectedFolderPath,
    showToast,
  ])

  const openMoveFolderModal = useCallback(() => {
    if (folderMoveBlocked) {
      showToast({
        message: isUnderReferenceUploadSubtreeUiPath(selectedFolderPath)
          ? '该目录不可移动'
          : '根目录不可移动',
        variant: 'warning',
      })
      return
    }
    closeLibraryLightbox()
    setMoveFolderTargetPath('')
    setMoveFolderOpen(true)
  }, [folderMoveBlocked, selectedFolderPath, showToast])

  const submitMoveFolder = useCallback(async () => {
    if (folderMoveBlocked) return
    const path = normalizeLibraryUiFolderPath(selectedFolderPath)
    const targetRaw = normalizeLibraryUiFolderPath(moveFolderTargetPath)
    // 不能选自己或自己的子目录
    if (targetRaw === path) {
      showToast({ message: '不能移动到自己里面', variant: 'warning' })
      return
    }
    if (targetRaw.startsWith(path + '/')) {
      showToast({ message: '不能移动到自己的子目录里', variant: 'warning' })
      return
    }
    await runBlocking(
      async () => {
        try {
          await moveReferenceFolder(path, targetRaw)
          const leaf = path.split('/').filter(Boolean).pop() ?? path
          const resolved = targetRaw ? `${targetRaw}/${leaf}` : leaf
          showToast({ message: '文件夹已移动', variant: 'success' })
          setMoveFolderOpen(false)
          setSelectedFolderPath(resolved)
          void refreshLibraryData()
        } catch (e) {
          showToast({ message: isApiError(e) ? e.message : '移动文件夹失败', variant: 'error' })
        }
      },
      {
        title: '移动文件夹',
        message: '正在迁移文件与占位…',
        detail: `「${path}」→「${targetRaw || '(根目录)'}」`,
      },
    )
  }, [
    folderMoveBlocked,
    moveFolderTargetPath,
    refreshLibraryData,
    runBlocking,
    selectedFolderPath,
    showToast,
  ])

  const submitMove = useCallback(async () => {
    if (!selectedItem) return
    if (moveDestPath === moveSourcePath) {
      showToast({ message: '请选择其它文件夹', variant: 'warning' })
      return
    }
    await runBlocking(
      async () => {
        try {
          await moveReferenceImage(selectedItem.publicId, moveDestPath)
          showToast({ message: '移动成功', variant: 'success' })
          setMoveOpen(false)
          void refreshLibraryData()
        } catch (e) {
          showToast({ message: isApiError(e) ? e.message : '移动失败', variant: 'error' })
        }
      },
      {
        title: '移动图片',
        message: '正在移动到目标目录，请稍候…',
        detail: moveDestPath || '图库根目录',
      },
    )
  }, [moveDestPath, moveSourcePath, refreshLibraryData, runBlocking, selectedItem, showToast])

  const confirmDeleteImage = useCallback(async () => {
    if (!selectedItem) return
    await runBlocking(
      async () => {
        try {
          await deleteReferenceImage(selectedItem.publicId)
          showToast({ message: '已删除', variant: 'success' })
          setDeleteImageOpen(false)
          setSelectedItem(null)
          void refreshLibraryData()
        } catch (e) {
          showToast({ message: isApiError(e) ? e.message : '删除失败', variant: 'error' })
        }
      },
      {
        title: '删除图片',
        message: '正在删除图片，请稍候…',
        detail: selectedItem.name,
      },
    )
  }, [refreshLibraryData, runBlocking, selectedItem, showToast])

  const confirmDeleteFolder = useCallback(async () => {
    if (!selectedFolderPath) return
    if (isSystemReferenceUploadRootFolder(selectedFolderPath)) {
      showToast({ message: '系统参考图目录不可删除', variant: 'warning' })
      setDeleteFolderOpen(false)
      return
    }
    await runBlocking(
      async () => {
        try {
          await deleteReferenceFolder(selectedFolderPath)
          showToast({ message: '文件夹已删除', variant: 'success' })
          setDeleteFolderOpen(false)
          setSelectedFolderPath('')
          void refreshLibraryData()
        } catch (e) {
          // 无论成功失败都关闭确认弹窗；失败信息用 toast 呈现，选中路径保留便于重试
          setDeleteFolderOpen(false)
          showToast({ message: isApiError(e) ? e.message : '删除文件夹失败', variant: 'error' })
        }
      },
      {
        title: '删除文件夹',
        message: '正在删除文件夹，请稍候…',
        detail: selectedFolderPath,
      },
    )
  }, [refreshLibraryData, runBlocking, selectedFolderPath, showToast])

  const syncCloudIndex = useCallback(async () => {
    await runBlocking(
      async () => {
        try {
          const res = await syncReferenceCloudIndex()
          const detail = `扫描 ${res.scanned} 张，新增索引 ${res.inserted} 张，跳过 ${res.skipped} 张`
          showToast({ message: `云端索引同步完成：${detail}`, variant: 'success', autoCloseMs: 5000 })
          void refreshLibraryData(selectedFolderPath)
        } catch (e) {
          showToast({ message: isApiError(e) ? e.message : '云端索引同步失败', variant: 'error' })
        }
      },
      {
        title: '同步云端图库',
        message: '正在扫描云端并回补索引，请稍候…',
      },
    )
  }, [refreshLibraryData, runBlocking, selectedFolderPath, showToast])

  const hasMore = items.length > 0 && items.length < total
  const showInitialLoading =
    loading && items.length === 0 && !error && !hasInitializedRef.current

  const buildLightboxItem = useCallback((img: ReferenceImageItem): ImageLightboxItem => {
    const wh =
      img.width != null && img.height != null && img.width > 0 && img.height > 0
        ? `${img.width} × ${img.height}`
        : ''
    const details: Array<{ label: string; value: string }> = [
      { label: '名称', value: (img.name || '未命名').trim() || '未命名' },
      { label: '大小', value: formatBytes(img.sizeBytes) },
      { label: '上传时间', value: formatGalleryDateTime(img.createdAt) },
    ]
    if (wh) details.push({ label: '像素尺寸', value: wh })
    if (img.aspectLabel?.trim()) details.push({ label: '比例', value: img.aspectLabel.trim() })
    if (img.modelLabel?.trim()) details.push({ label: '模型', value: img.modelLabel.trim() })
    if (img.sourceCreatedAt?.trim()) {
      details.push({ label: '生成时间', value: formatGalleryDateTime(img.sourceCreatedAt) })
    }
    const nameTrim = (img.name || '').trim()
    return {
      src: resolveReferenceImageDisplayUrl(img.url, {
        bucket: img.bucket,
        objectKey: img.objectKey,
      }),
      // 灯箱据此即时重签：打开/失败都换成新签名地址，不再硬用列表里的旧签名
      objectKey: img.objectKey,
      alt: img.name,
      taskLabel: '图库预览',
      subtitle: nameTrim || undefined,
      prompt: img.prompt?.trim() ?? '',
      details,
      referenceImageUrls: img.referenceImageUrls?.length ? [...img.referenceImageUrls] : undefined,
    }
  }, [])

  const openLibraryPreviewFromSelection = useCallback(() => {
    if (!selectedItem) return
    openLibraryLightbox([buildLightboxItem(selectedItem)], { sourcePublicId: selectedItem.publicId })
  }, [selectedItem, buildLightboxItem])

  /** 上传到当前选中目录——批量独立处理，单文件失败不影响其他 */
  const handleUploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files).filter((f) => f.type.startsWith('image/'))
      if (arr.length === 0) {
        showToast({ message: '请选择图片文件', variant: 'warning' })
        return
      }
      const oversized = arr.filter((f) => f.size > REFERENCE_IMAGE_MAX_BYTES)
      if (oversized.length > 0) {
        showToast({
          message: `${oversized.length} 张超过 8MB，已跳过`,
          variant: 'warning',
        })
      }
      const eligible = arr.filter((f) => f.size <= REFERENCE_IMAGE_MAX_BYTES)
      if (eligible.length === 0) return

      setIsUploading(true)
      const folderPath = selectedFolderPath
      let successCount = 0
      let failCount = 0
      let firstError: string | null = null
      try {
        for (const file of eligible) {
          try {
            await uploadGalleryImageToCloud(file, folderPath)
            successCount += 1
          } catch (e) {
            failCount += 1
            if (!firstError) firstError = isApiError(e) ? e.message : '上传失败'
          }
        }
        const parts: string[] = []
        if (successCount > 0) parts.push(`成功 ${successCount} 张`)
        if (failCount > 0) parts.push(`失败 ${failCount} 张`)
        showToast({
          message: parts.join('，') || '未选择有效图片',
          variant: failCount === 0 ? 'success' : failCount < eligible.length ? 'warning' : 'error',
          autoCloseMs: 4000,
        })
        if (successCount > 0) {
          void refreshLibraryData(folderPath)
        }
      } finally {
        setIsUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [refreshLibraryData, selectedFolderPath, showToast],
  )

  const triggerFilePicker = useCallback(() => {
    if (!canWriteLibrary || isUploading) return
    closeLibraryLightbox()
    fileInputRef.current?.click()
  }, [canWriteLibrary, isUploading, closeLibraryLightbox])

  const showErrorOnly = !!error && items.length === 0 && !loading
  const showEmpty = !loading && !error && items.length === 0

  const shouldIgnoreLibraryPullStart = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    return Boolean(target.closest('input, textarea, select, a[href], [contenteditable="true"]'))
  }, [])

  const libraryPullRefreshLockRef = useRef(false)

  const onLibraryPullRefreshCommit = useCallback(() => {
    if (libraryPullRefreshLockRef.current) return
    libraryPullRefreshLockRef.current = true
    void (async () => {
      try {
        const ok = await refreshLibraryData(selectedFolderPath)
        if (ok && location.pathname === '/library') {
          showToast({ message: '图库已更新', variant: 'success', autoCloseMs: 4000 })
        }
      } finally {
        libraryPullRefreshLockRef.current = false
      }
    })()
  }, [refreshLibraryData, location.pathname, showToast, selectedFolderPath])

  const {
    pullPx: libraryPullPx,
    commitFlying: libraryPullCommitFlying,
    onCommitFlyComplete: onLibraryPullCommitFlyComplete,
    releaseThreshold: libraryPullReleaseThreshold,
  } = useScrollPullToRefresh({
    scrollRef: libraryScrollRef,
    attachSignal: libraryPullAttachSignal,
    enabled: location.pathname === '/library' && !showInitialLoading,
    refreshing: loading || loadingMore,
    shouldIgnorePullStart: shouldIgnoreLibraryPullStart,
    onCommit: onLibraryPullRefreshCommit,
  })

  const navigateFolder = useCallback((path: string) => {
    setSelectedFolderPath(path)
    setSelectedItem(null)
  }, [])

  const libraryBreadcrumbs = useMemo(
    () => buildLibraryPageBreadcrumbs(selectedFolderPath, navigateFolder),
    [selectedFolderPath, navigateFolder],
  )

  /**
   * 补签失败提示节流：整屏图片同时失败时会并发打出几十个 refresh-url，
   * 若不节流会糊一脸 toast；同一 root cause 只提示一次。
   */
  const lastRefreshErrorToastAtRef = useRef(0)

  const refreshImageUrlByKey = useCallback(
    async (target: ReferenceImageItem): Promise<ReferenceImageItem | null> => {
      if (!target.objectKey) return null
      // 走统一调度：列表卡片 / 选择器 / 灯箱同时失败时不会打出几十个并发请求
      return refreshReferenceUrlOnce(
        target.objectKey,
        async (objectKey) => {
          try {
            const refreshed = await refreshReferenceImageUrlByObjectKey(objectKey)
            setItems((prev) =>
              prev.map((item) =>
                item.publicId === refreshed.publicId ? { ...item, ...refreshed } : item,
              ),
            )
            setSelectedItem((prev) =>
              prev && prev.publicId === refreshed.publicId ? { ...prev, ...refreshed } : prev,
            )
            return refreshed
          } catch (e) {
            const now = Date.now()
            if (now - lastRefreshErrorToastAtRef.current > 15_000) {
              lastRefreshErrorToastAtRef.current = now
              showToast({
                message: isApiError(e) ? e.message : '图片地址刷新失败，请稍后重试',
                variant: 'warning',
              })
            }
            return null
          }
        },
        { force: true },
      )
    },
    [showToast],
  )

  const headerBelow = (
    <LibraryManagementToolbar
      angular={angular}
      filteredCount={filtered.length}
      selectedFolderPath={selectedFolderPath}
      selectedItem={selectedItem}
      managementPanelOpen={libraryManagementOpen}
      onToggleManagementPanel={() => setLibraryManagementOpen((v) => !v)}
      onNavigateFolder={navigateFolder}
      onOpenFolderDrawer={() => {
        closeLibraryLightbox()
        setFolderDrawerOpen(true)
      }}
      onOpenDeleteFolder={openDeleteFolderModal}
      onOpenRenameFolder={openRenameFolderModal}
      onOpenRename={openRenameModal}
      onOpenMove={openMoveModal}
      onOpenDeleteImage={openDeleteImageModal}
      onOpenPreview={openLibraryPreviewFromSelection}
      uploadSlot={
        <ChromeToolbarButton
          type="button"
          onClick={triggerFilePicker}
          disabled={!canWriteLibrary || isUploading}
          title={isUploading ? '上传中…' : '上传图片到当前目录'}
        >
          {isUploading ? '上传中…' : '上传图片'}
        </ChromeToolbarButton>
      }
      deleteFolderDisabled={folderDeleteBlocked}
      renameFolderDisabled={folderRenameBlocked}
      writeDisabled={!canWriteLibrary}
    />
  )

  const headerTrailing = (
    <ChromeToolbarButton
      type="button"
      className="h-9 lg:hidden"
      onClick={() => void syncCloudIndex()}
      disabled={blocking || !canWriteLibrary}
    >
      同步云端图库
    </ChromeToolbarButton>
  )

  return (
    <>
      <ContentSkeleton
        title="图库"
        breadcrumbs={libraryBreadcrumbs}
        headerTrailing={headerTrailing}
        adminLayout
        framed={false}
        scrollableBody={false}
        shellScrollable={false}
        headerBelow={headerBelow}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:h-full lg:min-h-0 lg:flex-row">
          <aside
            className={[
              'shell-rail hidden h-full min-h-0 w-56 shrink-0 flex-col bg-[var(--shell-rail-bg)] lg:flex xl:w-[15.5rem]',
              angular ? 'border-r-2 border-[var(--shell-nav-border)]' : 'border-r border-[var(--shell-rail-border)]',
            ].join(' ')}
          >
            <LibraryFolderSidebarToolbar
              angular={angular}
              onNewFolder={() => void openCreateFolderModal()}
              newFolderDisabled={createFolderBlocked}
              writeDisabled={!canWriteLibrary}
            />
            <div className="scroll-region flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-2 lg:pe-3">
              <LibraryFolderTreeView
                model={treeModel}
                selectedPath={selectedFolderPath}
                onSelect={setSelectedFolderPath}
                angular={angular}
                showLibraryRootRow
                fillRemainingHeight
              />
            </div>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col px-4 pb-4 pt-2 sm:px-6 lg:min-h-0 lg:overflow-hidden lg:px-5 lg:pb-6 lg:pt-2 xl:px-6">
            <div
              ref={setLibraryScrollPort}
              key={`library-view-${selectedFolderPath}`}
              className="scroll-region flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overscroll-y-contain pb-2 pt-1 sm:pb-4 sm:pt-2"
            >
              <div
                className="will-change-transform flex min-h-0 min-w-0 flex-1 flex-col"
                style={{ transform: `translate3d(0, ${libraryPullPx}px, 0)` }}
              >
                <div className="select-none" aria-live="polite">
                  {(loading && !showInitialLoading) ||
                  loadingMore ||
                  libraryPullPx >= 8 ||
                  libraryPullCommitFlying ? (
                    <TimelinePullIndicator
                      pullPx={
                        libraryPullCommitFlying ? libraryPullReleaseThreshold : libraryPullPx
                      }
                      releaseThreshold={libraryPullReleaseThreshold}
                      angular={angular}
                      commitFly={libraryPullCommitFlying}
                      onCommitFlyComplete={onLibraryPullCommitFlyComplete}
                      refreshing={(loading && !showInitialLoading) || loadingMore}
                    />
                  ) : (
                    <div className="h-0 min-h-px shrink-0" aria-hidden />
                  )}
                </div>
                {showErrorOnly ? (
                  <ThemedLibraryState
                    theme={theme}
                    kind="error"
                    detail={error ?? undefined}
                    onRetry={() => void refreshLibraryData()}
                  />
                ) : showInitialLoading ? (
                  <AsyncPullLoadingIndicator
                    angular={angular}
                    label={theme === 'geek' ? 'loading_assets…' : '正在加载图库…'}
                    className="pb-4"
                  />
                ) : showEmpty ? (
                  <ThemedLibraryState theme={theme} kind="empty" />
                ) : (
                  <>
                    <LibraryAssetsPane
                      theme={theme}
                      filtered={filtered}
                      angular={angular}
                      selectedPublicId={selectedItem?.publicId ?? null}
                      scrollRootRef={libraryScrollRef}
                      scrollRootAttachSignal={libraryPullAttachSignal}
                      managementPanelOpen={libraryManagementOpen}
                      onSelect={setSelectedItem}
                      onRefreshImageUrl={refreshImageUrlByKey}
                      lightboxOccludedPublicId={libraryLightboxSourcePublicId}
                      onOpenLightbox={(img) =>
                        openLibraryLightbox([buildLightboxItem(img)], { sourcePublicId: img.publicId })
                      }
                    />

                    {hasMore && !showInitialLoading ? (
                      <>
                        <ScrollProximitySentinel
                          rootRef={libraryScrollRef}
                          axis="vertical"
                          distancePx={320}
                          disabled={loadingMore || loading}
                          onTrigger={throttledLoadMore}
                        />
                        <p className="mt-3 pb-2 text-center text-xs text-ink-soft">
                          {loadingMore ? '加载更多中…' : `已加载 ${items.length} / ${total}，下滑自动加载`}
                        </p>
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>

          <LibraryManagementPanel
            open={libraryManagementOpen}
            angular={angular}
            selectedItem={selectedItem}
            selectedFolderPath={selectedFolderPath}
            onNavigateFolder={navigateFolder}
            onOpenRename={openRenameModal}
            onOpenMove={openMoveModal}
            onOpenDeleteImage={openDeleteImageModal}
            onOpenDeleteFolder={openDeleteFolderModal}
            onOpenRenameFolder={openRenameFolderModal}
            onOpenPreview={openLibraryPreviewFromSelection}
            onSyncCloud={() => void syncCloudIndex()}
            syncCloudDisabled={blocking}
            deleteFolderDisabled={folderDeleteBlocked}
            renameFolderDisabled={folderRenameBlocked}
            moveFolderDisabled={folderMoveBlocked}
            writeDisabled={!canWriteLibrary}
            onUpload={triggerFilePicker}
            uploadDisabled={!canWriteLibrary}
            uploadLoading={isUploading}
            onMoveFolder={openMoveFolderModal}
          />
        </div>
      </ContentSkeleton>

      <LibraryFolderTreeDrawer
        open={folderDrawerOpen}
        onClose={() => setFolderDrawerOpen(false)}
        model={treeModel}
        selectedPath={selectedFolderPath}
        onSelect={setSelectedFolderPath}
        angular={angular}
        onRequestNewFolder={(parent) => void openCreateFolderModal(parent)}
      />

      <ThemeModal
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        title="新建文件夹"
        angular={angular}
        size="lg"
        bodyClassName="px-3 py-3 sm:px-5 sm:py-5"
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setCreateFolderOpen(false)}>
              取消
            </ChromeToolbarButton>
            <ChromePrimaryButton
              type="button"
              density="compact"
              disabled={
                blocking ||
                isUnderReferenceUploadSubtreeUiPath(createFolderParentPath) ||
                !createFolderDraft.trim()
              }
              onClick={() => void submitCreateFolder()}
            >
              创建
            </ChromePrimaryButton>
          </div>
        }
      >
        <p className="mb-2 text-sm text-ink-soft">先点选上级目录，再填写新文件夹名称（单层，勿含 /）。</p>
        {isUnderReferenceUploadSubtreeUiPath(createFolderParentPath) ? (
          <p className="mb-2 rounded-lg border border-line bg-surface-muted/50 px-3 py-2 text-xs text-ink-soft">
            当前上级为本地「上传的参考图」目录或其子路径，不能在此下新建文件夹；请选「图库根目录」或其它云图库目录。
          </p>
        ) : null}
        <div
          className={[
            'scroll-region mb-4 max-h-[min(40vh,260px)] min-h-0 overflow-y-auto overscroll-y-contain border border-line bg-panel p-2 pe-3',
            angular ? 'rounded-none' : 'rounded-xl',
          ].join(' ')}
        >
          <LibraryFolderTreeView
            model={treeModel}
            selectedPath={createFolderParentPath}
            onSelect={setCreateFolderParentPath}
            angular={angular}
            showLibraryRootRow
            omitLocalReferenceUpload
          />
        </div>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft">文件夹名称</span>
          <ChromeTextInput
            value={createFolderDraft}
            onChange={(e) => setCreateFolderDraft(e.target.value)}
            placeholder="例如：素材"
            className="w-full"
          />
        </label>
        {createFolderPathPreviewDisplay ? (
          <div className="mt-3 rounded-lg border border-line bg-surface-muted/40 px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-soft">完整路径预览</p>
            <p
              className="mt-1 break-all text-sm font-medium leading-relaxed text-ink"
              title={createFolderPathPreviewDisplay}
            >
              {createFolderPathPreviewDisplay}
            </p>
          </div>
        ) : null}
      </ThemeModal>

      <ThemeModal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="重命名"
        angular={angular}
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setRenameOpen(false)}>
              取消
            </ChromeToolbarButton>
            <ChromePrimaryButton type="button" density="compact" disabled={blocking} onClick={() => void submitRename()}>
              保存
            </ChromePrimaryButton>
          </div>
        }
      >
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft">文件名</span>
          <ChromeTextInput
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            className="w-full"
            autoFocus
          />
        </label>
      </ThemeModal>

      <ThemeModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        title="移动到文件夹"
        angular={angular}
        size="lg"
        bodyClassName="px-3 py-3 sm:px-4 sm:py-4"
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setMoveOpen(false)}>
              取消
            </ChromeToolbarButton>
            <ChromePrimaryButton
              type="button"
              density="compact"
              disabled={blocking || moveDestPath === moveSourcePath}
              onClick={() => void submitMove()}
            >
              移动到此
            </ChromePrimaryButton>
          </div>
        }
      >
        <p className="mb-3 text-sm text-ink-soft">点击目标目录；「图库根目录」表示存放在顶层。</p>
        <LibraryFolderTreeView
          model={treeModel}
          selectedPath={moveDestPath}
          onSelect={setMoveDestPath}
          angular={angular}
          showLibraryRootRow
        />
      </ThemeModal>

      <ThemeModal
        open={deleteImageOpen}
        onClose={() => setDeleteImageOpen(false)}
        title="删除图片"
        angular={angular}
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setDeleteImageOpen(false)}>
              取消
            </ChromeToolbarButton>
            <ChromeToolbarDangerButton type="button" className="px-4 py-2" disabled={blocking} onClick={() => void confirmDeleteImage()}>
              删除
            </ChromeToolbarDangerButton>
          </div>
        }
      >
        <p className="text-sm text-ink">
          确定删除「<span className="font-medium">{selectedItem?.name ?? ''}</span>」？此操作不可恢复。
        </p>
      </ThemeModal>

      <ThemeModal
        open={renameFolderOpen}
        onClose={() => setRenameFolderOpen(false)}
        title="重命名文件夹"
        angular={angular}
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setRenameFolderOpen(false)}>
              取消
            </ChromeToolbarButton>
            <ChromePrimaryButton
              type="button"
              density="compact"
              disabled={blocking || !renameFolderDraft.trim()}
              onClick={() => void submitRenameFolder()}
            >
              保存
            </ChromePrimaryButton>
          </div>
        }
      >
        <p className="mb-2 text-sm text-ink-soft">仅修改当前文件夹名称（单层，勿含 /）；其下图片与子文件夹会随路径一起迁移。</p>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft">新名称</span>
          <ChromeTextInput
            value={renameFolderDraft}
            onChange={(e) => setRenameFolderDraft(e.target.value)}
            placeholder="文件夹名称"
            className="w-full"
            autoFocus
          />
        </label>
        {renameFolderPathPreviewDisplay ? (
          <div className="mt-3 rounded-lg border border-line bg-surface-muted/40 px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-soft">完整路径预览</p>
            <p
              className="mt-1 break-all text-sm font-medium leading-relaxed text-ink"
              title={renameFolderPathPreviewDisplay}
            >
              {renameFolderPathPreviewDisplay}
            </p>
          </div>
        ) : null}
      </ThemeModal>

      <ThemeModal
        open={moveFolderOpen}
        onClose={() => setMoveFolderOpen(false)}
        title="移动文件夹"
        angular={angular}
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setMoveFolderOpen(false)}>
              取消
            </ChromeToolbarButton>
            <ChromePrimaryButton
              type="button"
              density="compact"
              disabled={blocking}
              onClick={() => void submitMoveFolder()}
            >
              移动
            </ChromePrimaryButton>
          </div>
        }
      >
        <p className="mb-2 text-sm text-ink-soft">
          将「<span className="font-medium">{normalizeLibraryUiFolderPath(selectedFolderPath) || '(根目录)'}</span>」整体移动到新父目录下；其子目录与图片一并迁移。
        </p>
        <MoveFolderTargetPicker
          currentPath={normalizeLibraryUiFolderPath(selectedFolderPath)}
          knownPaths={knownFolderPaths}
          value={moveFolderTargetPath}
          onChange={setMoveFolderTargetPath}
          angular={angular}
        />
      </ThemeModal>

      <ThemeModal
        open={deleteFolderOpen}
        onClose={() => setDeleteFolderOpen(false)}
        title="删除文件夹"
        angular={angular}
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setDeleteFolderOpen(false)}>
              取消
            </ChromeToolbarButton>
            <ChromeToolbarDangerButton type="button" className="px-4 py-2" disabled={blocking} onClick={() => void confirmDeleteFolder()}>
              删除
            </ChromeToolbarDangerButton>
          </div>
        }
      >
        <p className="text-sm text-ink">
          确定删除文件夹「<span className="font-medium">{selectedFolderPath}</span>」？仅空文件夹可删除。
        </p>
      </ThemeModal>

      <BlockingModal
        open={blockingState.open}
        title={blockingState.title}
        message={blockingState.message}
        detail={blockingState.detail}
      />

      {/* 图库图片上传：隐藏 input，点击工具栏按钮触发 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = event.target.files
          if (files && files.length > 0) void handleUploadFiles(files)
        }}
      />
    </>
  )
}

/** Modal 内嵌：让用户选一个目标父目录（根目录 + 所有已知文件夹，过滤掉自己和自己的子目录） */
function MoveFolderTargetPicker({
  currentPath,
  knownPaths,
  value,
  onChange,
  angular,
}: {
  currentPath: string
  knownPaths: ReadonlySet<string>
  value: string
  onChange: (next: string) => void
  angular: boolean
}) {
  const options = useMemo(() => {
    const out: { path: string; label: string }[] = [{ path: '', label: '(根目录)' }]
    for (const p of knownPaths) {
      const n = normalizeLibraryUiFolderPath(p)
      if (!n) continue
      // 不能选自己或自己的子目录
      if (n === currentPath) continue
      if (currentPath && n.startsWith(currentPath + '/')) continue
      out.push({ path: n, label: n })
    }
    out.sort((a, b) => a.label.localeCompare(b.label))
    return out
  }, [currentPath, knownPaths])

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft">目标父目录</span>
      <ChromeTextInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="输入或选择目标路径"
        className="w-full"
        list="library-move-folder-options"
      />
      <datalist id="library-move-folder-options">
        {options.map((o) => (
          <option key={o.path} value={o.path} label={o.label} />
        ))}
      </datalist>
      <div className="mt-1 max-h-36 overflow-auto rounded-lg border border-line bg-surface-muted/40">
        {options.length === 0 ? (
          <p className="px-3 py-2 text-xs text-ink-soft">暂无可选目录</p>
        ) : (
          <ul className="divide-y divide-line/60">
            {options.map((o) => (
              <li key={o.path}>
                <button
                  type="button"
                  onClick={() => onChange(o.path)}
                  className={[
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                    angular ? '' : 'hover:bg-surface-muted/60',
                    value === o.path ? 'bg-surface-muted font-semibold text-ink' : 'text-ink/85',
                  ].join(' ')}
                >
                  <span className="shrink-0">📁</span>
                  <span className="truncate" title={o.label}>{o.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
